// FrameBox — renders children inside an <iframe> sized to a fixed width, so CSS
// media queries respond to THAT width (a plain width-capped <div> can't — Tailwind
// breakpoints key off the viewport). Parent stylesheets are cloned into the frame
// and its height auto-fits the content. Used by the Module Library to preview each
// module at standard breakpoints (375 / 768 / 1280).
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface FrameBoxProps {
  width: number;
  children: React.ReactNode;
}

export const FrameBox: React.FC<FrameBoxProps> = ({ width, children }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(320);

  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;

    // Clone the host page's styles (Tailwind, Slate tokens, @font-face) so the
    // module looks identical inside the frame.
    doc.head.innerHTML = "";
    document
      .querySelectorAll('style, link[rel="stylesheet"]')
      .forEach((node) => doc.head.appendChild(node.cloneNode(true)));
    doc.documentElement.style.background = "#ffffff";
    doc.body.style.margin = "0";
    setBody(doc.body);

    const observer = new ResizeObserver(() => {
      setHeight(doc.body.scrollHeight);
    });
    observer.observe(doc.body);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <iframe
        ref={frameRef}
        title="Module preview"
        scrolling="no"
        style={{
          width,
          height,
          maxWidth: "100%",
          border: "1px solid var(--slate-color-border-muted, #e5e5e5)",
          borderRadius: 8,
          display: "block",
          margin: "0 auto",
          background: "#ffffff",
        }}
      />
      {body ? createPortal(children, body) : null}
    </>
  );
};
